import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';

import { FlowOutputStepRunGrid, FlowOutputStepRunGridScope } from '../../app/content/FlowOutputStepRunGrid';
import { AngularDirective } from '../../common/react/components/AngularDirective';
import {
  AngularToReactDataGridBridge,
  IDataGridPropsScope,
} from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { FilterPanelTypeaheadScope, IFilterPanelContainerScope } from '../../ts/controllers/FilterPanelCtrl';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { SubplaceWithActionButtonsScope } from '../../ts/helpers/ActionButtonsHelper';
import { FlowOutputStepRunContentType } from '../../ts/models/ContentTypes';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { IEmptyTemplateModel } from '../../ts/models/IEmptyTemplate';
import { ISubplace } from '../../ts/models/IPlace';
import { ISliceable } from '../../ts/models/ISliceable';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { FilterPanelService } from '../../ts/services/FilterPanelService';
import { IQueryUpdater, QueryService } from '../../ts/services/QueryService';
import { ServerService } from '../../ts/services/ServerService';
import { SorterFactory } from '../../ts/services/SorterFactory';
import { FlowRunEvent } from '../utils/FlowEvents';

type FlowRunHistoryDataGridViewScope =
  IDataGridPropsScope<FlowOutputStepRunListItemInfo> &
  IFilterPanelContainerScope &
  FilterPanelTypeaheadScope &
  FlowOutputStepRunGridScope &
  SubplaceWithActionButtonsScope<FlowOutputStepRunListItemInfo, {}> &
{
  sliceable: ISliceable<FlowOutputStepRunListItemInfo>;
  updateFlowRunHistoryGridTimer: any;
};

const updateRunHistoryGridIntervalSeconds = 60;

const template = `<div ng-include="'dataGridSubplaceWithFilterPanel.html'"></div>`;

const flowRunsEmptyTemplateModel: IEmptyTemplateModel = {
  title: 'flowRunsEmptyMessage'
};

export type FlowRunHistoryDataGridViewProps = {
  readonly angularContext: AngularContext;
  readonly subplace: ISubplace<FlowOutputStepRunListItemInfo>
  readonly translate: IReactTranslateService;
  readonly scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter;
  readonly siteUrls: SiteUrls;
  readonly flowAllowedActions: FlowAllowedActions;
};

export class FlowRunHistoryDataGridView extends React.Component<FlowRunHistoryDataGridViewProps> {

  private angularToReactDataGridBridge: AngularToReactDataGridBridge;
  private filterPanelService: FilterPanelService;
  private queryService: QueryService;
  private serverService: ServerService;
  private sorterFactory: SorterFactory;
  private scope: FlowRunHistoryDataGridViewScope;

  constructor(props: FlowRunHistoryDataGridViewProps) {
    super(props);
    const angularContext = this.props.angularContext;
    this.angularToReactDataGridBridge = angularContext.getService('AngularToReactDataGridBridge');
    this.filterPanelService = angularContext.getService('FilterPanelService');
    this.queryService = angularContext.getService('QueryService');
    this.serverService = angularContext.getService('ServerService');
    this.sorterFactory = angularContext.getService('SorterFactory');
  }

  public componentWillUnmount() {
    this.scope.$destroy();
  }

  public render(): JSX.Element {
    return (
      <AngularDirective
        angularContext={this.props.angularContext}
        setupScope={this.setupScope}
        template={template}
      />
    );
  }

  private readonly setupScope = (scope: ng.IScope): ng.IScope => {
    this.scope = scope.$new() as FlowRunHistoryDataGridViewScope;
    this.configureForReactDataGrid(this.scope);

    this.scope.filterControls = {};

    const explicitFilterKeys = this.props.subplace.filterKeys();
    this.scope.filterDefinitions = this.filterPanelService.getFilterDefinitions(explicitFilterKeys);
    this.scope.getFieldValueQuery =
      (filterField: string) => this.props.subplace.filterValuesQuery(filterField).withMatchText('');
    this.scope.hideSearch = true;

    this.scope.sorter = this.sorterFactory.create(FlowOutputStepRunContentType.defaultOrder);

    this.scope.anyActionsAllowed = () => false;

    const queryUpdater: IQueryUpdater =
      this.queryService.createQueryUpdaterFromSubplace(
        this.scope,
        this.props.subplace,
        (resourceQuery) => {
          this.angularToReactDataGridBridge.updateReactDataGridRowStore({
            scope: this.scope,
            sliceable: resourceQuery
          });
          this.scope.sliceable = resourceQuery;
          this.scope.sorter.setOrder(resourceQuery.getOrder());
        }
      );

    queryUpdater.refresh();

    this.scope.$on(FlowRunEvent, () => queryUpdater.refresh());

    if (FeatureFlags.autoUpdateFlowGrids) {
      this.scope.updateFlowRunHistoryGridTimer =
      setInterval(() => {
        this.scope.$apply(() => queryUpdater.refresh());
      }, updateRunHistoryGridIntervalSeconds * 1000);
    }

    this.scope.$on('$destroy', () => {
      this.scope.sorter.detach();
      if (FeatureFlags.autoUpdateFlowGrids) {
        clearInterval(this.scope.updateFlowRunHistoryGridTimer);
      }
    });

    return this.scope;
  }

  private configureForReactDataGrid(
    scope: FlowRunHistoryDataGridViewScope
  ) {
    const columns = FlowOutputStepRunGrid.columns(
      scope,
      this.props.translate,
      this.props.scheduleFrequencyDescriptionFilter,
      this.props.siteUrls,
      this.serverService.isSessionSiteOrServerAdmin(),
      this.props.flowAllowedActions.download
    );
    this.angularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: scope,
      columns: columns,
      emptyChild: this.angularToReactDataGridBridge.emptyTemplateElementFor(flowRunsEmptyTemplateModel),
      canShowFilterPanel: FlowOutputStepRunContentType.canShowFilterPanel,
      rememberLastScrollPos: true,
    });
  }
}
